/** @jsxImportSource @emotion/react */
import type { ReactNode } from 'react';
import styled from '@emotion/styled';
import { ClassNames, css } from '@emotion/react';
import type { SerializedStyles } from '@emotion/react';
import ReactModal from 'react-modal';
import * as tokens from '@tokens';

import { ZINDEX } from '@utils/constants';
import { CloseAction } from '@components/Actions/CloseAction/CloseAction';
import { ModalHeader } from './ModalHeader';
import { ModalBody } from './ModalBody';
import { ModalFooter } from './ModalFooter';
import { useStrings } from '@hooks/useStrings';

export interface ModalProps
  extends Pick<
    ReactModal.Props,
    | 'onAfterOpen'
    | 'onAfterClose'
    | 'shouldCloseOnEsc'
    | 'shouldCloseOnOverlayClick'
  > {
  /**
   * Selector string for the element containing the web app. The app container
   * will be disabled with `aria-hidden` when modal is visible.
   */
  appElementSelector?: string;
  /**
   * Children prop from React.
   */
  children: ReactNode;
  /**
   * Styles override.
   */
  className?: string;
  /**
   * Accessible modal label visible only to screen readers.
   */
  contentLabel: string;
  /**
   * Hides the [X] close button.
   */
  hideCloseButton?: boolean;
  /**
   * Toggles modal visibility.
   */
  isOpen: boolean;
  /**
   * Callback for when the modal [X] close button is pressed, overlay is clicked
   * or ESC button pressed.
   * Use it in combination with `isOpen` to hide the modal.
   */
  onClose?: () => void;
  /**
   * Size of the modal. 4 sizes, to result in 384px, 592px, 768px width or `fullscreen` to cover the whole viewport.
   */
  size?: keyof typeof WIDTHS;
}

const WIDTHS = {
  s: '384px',
  m: '592px',
  l: '768px',
  fullscreen: '100vw',
} as const;

const overlayStyles = css`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  outline: 0;
  overflow-x: hidden;
  overflow-y: auto;
  background: ${tokens.color.ui_overlay};
  z-index: ${ZINDEX.MODAL};
  &.ReactModal__Overlay {
    opacity: 0;
    ${tokens.transition('opacity')};
  }

  &.ReactModal__Overlay--after-open {
    opacity: 1;
  }

  &.ReactModal__Overlay--before-close {
    opacity: 0;
  }
`;

interface StyledModalProps extends ReactModal.Props, Pick<ModalProps, 'size'> {}

const getSizeStyles = (size: keyof typeof WIDTHS): SerializedStyles => {
  switch (size) {
    case 'fullscreen':
      return css`
        border-radius: 0;
        height: 100vh;
        justify-content: space-between;
        margin: 0 auto;
        max-height: 100vh;
        max-width: 100vw;
      `;
    default:
      return css`
        border-radius: ${tokens.radius.l};
        margin: ${tokens.spacer.x8} auto ${tokens.spacer.x8};
        max-height: calc(100vh - ${tokens.spacer.x8} * 2);
        max-width: calc(100vw - ${tokens.spacer.x4});
      `;
  }
};

const StyledReactModal = styled(ReactModal, {
  shouldForwardProp: (prop: string) => prop !== 'size',
})<StyledModalProps>`
  display: flex;
  flex-direction: column;
  position: relative;
  min-height: 216px;
  min-width: 320px;
  width: ${({ size }) => WIDTHS[size]};
  ${tokens.elevation(4)};
  background-color: ${tokens.color.ui_background};
  outline: 0;

  ${({ size }) => getSizeStyles(size)};
`;

const CloseButton = styled(CloseAction)`
  position: absolute;
  top: ${tokens.spacer.x2};
  right: ${tokens.spacer.x2};
`;

let isAppElementSet = false;

/**
 * Modal displays content that requires user interaction, in a layer above the page.
 *
 * {@link https://zeroheight.com/76c439891/p/88298b-modal/b/639711 Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-modal--modal Code documentation}
 *
 * @example
 * <Modal isOpen={} onClose={} />
 */
export const Modal = ({
  appElementSelector = '#site-container',
  children,
  hideCloseButton = false,
  onClose,
  size = 'm',
  ...props
}: ModalProps): JSX.Element => {
  const { actions } = useStrings();
  if (!isAppElementSet && document.querySelector(appElementSelector)) {
    ReactModal.setAppElement(appElementSelector);
    isAppElementSet = true;
  }

  return (
    <ClassNames>
      {({ css: generateClassName }) => (
        <StyledReactModal
          {...props}
          overlayClassName={generateClassName(overlayStyles)}
          onRequestClose={onClose}
          size={size}
        >
          {!hideCloseButton && (
            <CloseButton description={actions.close} onClick={onClose} />
          )}
          {children}
        </StyledReactModal>
      )}
    </ClassNames>
  );
};

Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
